import React, {lazy, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Btn, FlexColumnSection, FlexDiv, FlexRowSection} from "../../../styles/globalStyles";
import FormInput from "../../../components/UI/FormInput";
import {checkIfDateAfterToday, isLocationIL, isUserFieldRequired} from "../../../helpers/functions";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {DB_DATE_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import WhatsNextBox from "../../../components/WhatsNextBox";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {t} from "i18next";
import {updateError} from "@arboxappv4/shared/src/redux/stepperSlice";
import {setError} from "@arboxappv4/shared/src/redux/siteSlice";
import styled from "styled-components";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import dayjs from "dayjs";
import CustomFieldSwitcher from "../../../components/UI/CustomFieldSwitcher";
import {useDispatch, useSelector} from "react-redux";
import CustomSelect from "../../../components/UI/CustomSelect";
import {USER_SITE_FIELDS_SETTINGS} from "../../../../../src/constants";
import {LINK_EXISTING_MEMBER_MODAL, openModal} from "@arboxappv4/shared/src/redux/modalManagerSlice";

const CustomDatePicker = lazy(() => import(/* webpackChunkName: "CustomDatePicker" */'../../../components/UI/CustomDatePicker'));

const PERSONAL_ID_CHECK_DEBOUNCE_MS = 400;
const LINKABLE_PERSONAL_ID_MESSAGE_KEY = 'personal-id-exists-link-group';
const ALREADY_IN_GROUP_MESSAGE_KEY = 'personal-id-already-in-another-group';

const isPersonalIdLookupValue = (personalId) => /^[0-9]{5,9}$/.test(String(personalId || '').trim());

const ChildDetails = (props) => {
    const { handleSubmit, values, errors, setFieldValue, touched, validationSchema, cfFlowName} = props;
    const [submitted, setSubmitted] = useState(false);
    const [errorNotified, setErrorNotified] = useState(false);
    const [personalIdError, setPersonalIdError] = useState(null);
    const [loading, setLoading] = useState(false);
    const linkedExisting = !!values.child?.link_existing
    const {isMobile} = useScreenSize()
    const box = useSelector(state => state.site.box)
    const userBox = useSelector(state => state.user.activeUsersBoxes)
    const flowName = useSelector(state => state.stepper.flowName)
    const dispatch = useDispatch()
    const customFieldsDataPerLocation = useMemo(() => box?.locations_box?.find(location => location.id === values.locations_box_fk)?.custom_field, [values.locations_box_fk,box]);
    const userFieldSettings = useMemo(() => box?.boxes_settings?.find(setting => setting.prop_name === USER_SITE_FIELDS_SETTINGS)?.properties, [box])
    const checkRequestIdRef = useRef(0)
    const debounceTimerRef = useRef(null)

    useEffect(() => {
        const flowCustomFields = customFieldsDataPerLocation?.filter(cf => cf?.custom_field_flow.some(cf_flow => cf_flow.flow.name === (cfFlowName)))
        const newFields = flowCustomFields?.map(field => {
            if(values.child?.customFields) {
                const old = values.child.customFields.find(oldField => oldField.id === field.id)
                if (old) {
                    return {...field, value: old.value}
                }
            }
            return field;
        })
        setTimeout(() => setFieldValue('child.customFields', newFields),10);
    }, [customFieldsDataPerLocation]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        }
    }, [])

    useEffect(() => {
        const hasErrors = Object.keys(errors.child || {}).length > 0
        if (!hasErrors) {
            setErrorNotified(false)
            return
        }
        if (submitted && !errorNotified) {
            setErrorNotified(true)
            dispatch(setError(t('error-mandatory-field')))
        }
    }, [errors, submitted, flowName, errorNotified, dispatch]);

    const getError = (propName) => {
        if(submitted && errors.child && errors.child[propName]) {
            return errors.child[propName]
        }
        return null
    }

    const submitStep = () => {
        setSubmitted(true)
        handleSubmit()
    }

    const confirmLinkExistingMember = (candidate) => {
        setFieldValue('child', {
            ...values.child,
            first_name: candidate.first_name || '',
            last_name: candidate.last_name || '',
            personal_id: candidate.personal_id || values.child?.personal_id || '',
            gender: candidate.gender || '',
            birthday: candidate.birthday || null,
            phone: values.child?.phone || '',
            link_existing: true,
        })
        setPersonalIdError(null)
        setTimeout(() => submitStep(), 10)
    }

    const openLinkExistingMemberPopup = (candidate, canLink) => {
        if (!candidate) return
        dispatch(openModal({
            modalName: LINK_EXISTING_MEMBER_MODAL,
            props: {
                candidate,
                canLink,
                onConfirm: canLink ? () => confirmLinkExistingMember(candidate) : undefined,
            }
        }))
    }

    const applyPersonalIdConflict = useCallback((result) => {
        const messageKey = result?.messageKey
        if (!messageKey) {
            setPersonalIdError(null)
            return false
        }
        const message = t(messageKey)
        if (messageKey === LINKABLE_PERSONAL_ID_MESSAGE_KEY && result?.candidate) {
            setPersonalIdError(
                <ActionLink onClick={() => openLinkExistingMemberPopup(result.candidate, true)}>
                    {message}
                </ActionLink>
            )
            return true
        }
        if (messageKey === ALREADY_IN_GROUP_MESSAGE_KEY && result?.candidate) {
            setPersonalIdError(
                <ActionLink onClick={() => openLinkExistingMemberPopup(result.candidate, false)}>
                    {message}
                </ActionLink>
            )
            openLinkExistingMemberPopup(result.candidate, false)
            return true
        }
        setPersonalIdError(message)
        return true
    }, [])

    const checkPersonalIdCandidate = useCallback(async (personalId) => {
        if (!isLocationIL() || !isPersonalIdLookupValue(personalId) || !box?.id) {
            return null
        }
        const parentPersonalId = values.personal_id || userBox?.personal_id
        if (parentPersonalId && personalId === parentPersonalId) {
            setPersonalIdError(t('user-box-exist-personal-id'))
            return { messageKey: 'user-box-exist-personal-id' }
        }

        const requestId = ++checkRequestIdRef.current
        try {
            const result = await apiAction('site/register/personalIdGroupCandidate', 'post', {
                personalId,
                boxId: box.id,
                parentUserFk: userBox?.user_fk ?? null,
            })
            if (requestId !== checkRequestIdRef.current) return null
            applyPersonalIdConflict(result)
            return result
        } catch (error) {
            if (requestId !== checkRequestIdRef.current) return null
            console.log('personalIdGroupCandidate error', error)
            setPersonalIdError(t('user-box-exist-personal-id'))
            return { messageKey: 'user-box-exist-personal-id' }
        }
    }, [applyPersonalIdConflict, box?.id, userBox?.personal_id, userBox?.user_fk, values.personal_id])

    const onPersonalIdChange = (val) => {
        setFieldValue('child.personal_id', val)
        setPersonalIdError(null)
        if (values.child?.link_existing) {
            setFieldValue('child.link_existing', false)
        }
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = setTimeout(() => {
            checkPersonalIdCandidate(val)
        }, PERSONAL_ID_CHECK_DEBOUNCE_MS)
    }

    const validatePersonalIdAndSubmit = async () => {
        setSubmitted(true)
        try {
            const childErrors = errors.child || {};
            if (!linkedExisting && Object.keys(childErrors).length > 0) {
                if (childErrors.personal_id) setPersonalIdError(childErrors.personal_id)
                return
            }
            if (personalIdError && !linkedExisting) {
                return
            }
            setLoading(true)
            try {
                await apiAction('hugim/checkHugRestrictions', 'post', {
                    series_id: values.course?.id,
                    birthday: values.child?.birthday,
                })
            } catch(error) {
                if(error?.error?.messageToUser) dispatch(updateError(error.error.messageToUser))
                setLoading(false)
                return
            }
            if (isLocationIL() && values.child.personal_id) {
                const personalIdResult = await checkPersonalIdCandidate(values.child.personal_id)
                const messageKey = personalIdResult?.messageKey
                if (linkedExisting) {
                    if (messageKey !== LINKABLE_PERSONAL_ID_MESSAGE_KEY || !personalIdResult?.candidate) {
                        setLoading(false)
                        return
                    }
                } else if (personalIdResult?.candidate || messageKey) {
                    setLoading(false)
                    return
                }
            } else if (linkedExisting) {
                setLoading(false)
                return
            }
            setLoading(false)
            submitStep()
        } catch(error) {
            setLoading(false)
            console.log('error', error)
        }
    }

    const inputLabel = (name, label) => {
        const rawChildSchema = validationSchema?.fields?.child;
        const resolvedChildSchema = typeof rawChildSchema?.resolve === 'function'
            ? rawChildSchema.resolve({ parent: values })
            : rawChildSchema;
        const fields = resolvedChildSchema?.fields || {};
        if(resolvedChildSchema && fields && fields[name] && !fields[name].spec?.optional) {
            return `${label}*`
        } else if(isUserFieldRequired(userFieldSettings, name, 'child')) {
            return `${label}*`
        }
        return label
    }

    if(!values.child) return null

    return (
        <FlexRowSection justify={'space-between'} gap={'40px'} height={'100%'} align={'start'}>
            <FlexColumnExtended gap={'20px'} isMobile={isMobile} height={'100%'}>
                <FlexColumnSection gap={'24px'} height={isMobile ? `calc(100% - ${25+38}px)` : 'auto'} overflow={'auto'} flex={1}>
                    {linkedExisting && (
                        <LinkedBanner>
                            {t('personal-id-linked-member-banner', {
                                defaultValue: 'Existing client selected — they will be added to the group after purchase',
                            })}
                        </LinkedBanner>
                    )}
                    <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'20px'}>
                        <FormInput label={inputLabel('first_name', t('first-name'))} value={values.child.first_name} error={getError('first_name')} onChangeCallback={(val) => setFieldValue('child.first_name', val)} autoComplete={'given-name'} id="firstName" flex={1} disabled={linkedExisting}/>
                        <FormInput label={inputLabel('last_name', t('last-name'))} value={values.child.last_name} error={getError('last_name')} onChangeCallback={(val) => setFieldValue('child.last_name', val)} autoComplete={'family-name'} id="lastName" flex={1} disabled={linkedExisting}/>
                    </FlexDiv>
                    <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'20px'}>
                        {isLocationIL() && <FormInput label={inputLabel('personal_id', t('personal-id'))} value={values.child.personal_id} error={getError('personal_id') || personalIdError} onChangeCallback={onPersonalIdChange} type={'phone'} flex={1} inputMode={'tel'} disabled={linkedExisting}/>}
                        <FormInput label={inputLabel('phone', t('child-phone'))} value={values.child.phone} error={getError('phone')} onChangeCallback={(val) => setFieldValue('child.phone', val)} type={'phone'} autoComplete={'phone'} id={'phone'} flex={1} inputMode={'tel'} disabled={linkedExisting}/>
                    </FlexDiv>
                    <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'20px'}>
                        <FormInput label={inputLabel('birthday', t('birth-date'))} value={values.child.birthday} error={getError('birthday')} flex={1} showOptional={true}>
                            <CustomDatePicker onDateSelect={(val) => setFieldValue('child.birthday', val)}
                                              disabledDate={checkIfDateAfterToday}
                                              className={`custom-date ${getError('birthday') && 'custom-date-error'}`}
                                              validRange={[dayjs('1920-01-01',DB_DATE_FORMAT), dayjs()]}
                                              disabled={linkedExisting}
                            />
                        </FormInput>
                        <FormInput showOptional={true} label={inputLabel('gender', t('gender'))} value={values.child.gender} error={getError('gender')} flex={1}>
                            <CustomSelect className={`custom-select ${getError('gender') && 'custom-select-error'}`} allowClear filterSort={(a,b) => a - b} onChange={(val) => setFieldValue('child.gender', val)} options={[{ label: t('male'), value: 'male' }, { label: t('female'), value: 'female' }, {label: t('other'), value: 'other'}]} defaultValue={values.child.gender} disabled={linkedExisting} />
                        </FormInput>
                    </FlexDiv>
                    {values.child.customFields && !linkedExisting && <CustomFieldSwitcher data={values.child.customFields} setFieldValue={setFieldValue} touched={touched} errors={errors} isChild/> }
                </FlexColumnSection>
                <FlexRowSection height={'38px'} flex={'none'} justify={'end'}>
                    <Btn type={'primary'} onClick={validatePersonalIdAndSubmit} width={isMobile ? '100%' : null} loading={loading}>{t('continue')}</Btn>
                </FlexRowSection>
            </FlexColumnExtended>
            {!isMobile && <WhatsNextBox text={t('course-child-details-whats-next')}/>}
        </FlexRowSection>
    );
};

export default ChildDetails;

const FlexColumnExtended = styled(FlexColumnSection)`
    padding: ${({isMobile}) => isMobile ? '0 16px' : '20px'};
    background: ${({isMobile}) => isMobile ? 'none' : Colors.white};
    box-sizing: border-box;
    border-radius: 5px;
`;

const ActionLink = styled.span`
    text-decoration: underline;
    font-size: 14px;
    font-weight: 400;
    color: ${Colors.siteBlack};
    cursor: pointer;
`;

const LinkedBanner = styled.div`
    background: #F5F5F5;
    color: ${Colors.siteBlack};
    border-radius: 5px;
    padding: 12px 14px;
    font-size: 14px;
`;
